import { css, useTheme } from "@emotion/react";
import { lighten, rem, transparentize } from "polished";
import React, { useState } from "react";
import { useRecoilValue } from "recoil";

import { Flex, Label, Spacer } from "../../base";
import { appDataAtomByPoolType } from "../../modules/apps";
import { useApyByApp } from "../../modules/hooks";
import { AmpMark } from "../Icons";
import LogoIcon from "../LogoIcon";

const flexaLogoTag = "/assets/img/transformer-tag-flexa-noshadow.png";

const AppButton = ({
  selected,
  disabled = false,
  name,
  action = null,
  apy = "5.75",
  logo,
  iconProps = null,
  ...props
}) => {
  const { color } = useTheme();
  return (
    <Flex
      column
      height={action ? 210 : 180}
      width={140}
      {...props}
      css={css`
        margin: 0 ${rem(11)};
        border: ${rem(3)} solid
          ${selected ? transparentize(0.2, color.highlight) : "transparent"};
        border-radius: ${rem(24)};
        cursor: pointer;
        min-width: ${rem(140)};

        transition: 0.1s border-color;

        &:first-of-type {
          margin-left: 0;
        }
        &:last-of-type {
          margin-right: 0;
        }

        ${!disabled &&
        !selected &&
        css`
          &:hover {
            border-color: ${transparentize(0.5, color.highlight)};
          }
        `}

        ${disabled &&
        css`
          opacity: 0.3;
        `}
      `}
    >
      <LogoIcon logo={logo} {...iconProps} />
      <Spacer size={20} />
      {action ? (
        <>
          <Label light>{action}</Label>
          <Spacer size={6} />
        </>
      ) : null}
      <Label>{name}</Label>
      <Spacer size={14} />
      <Label light small>
        {apy}% APY
      </Label>
    </Flex>
  );
};

const AppButtonWithAPY = ({ appId, ...props }: any) => {
  const apy = useApyByApp(appId);
  return <AppButton {...props} apy={apy} />;
};

const AppSelector = ({
  selected = "",
  onSelect,
  disabledAppIds = [],
  includeSelf = false,
  actionText = "",
}) => {
  const { color } = useTheme();
  const appData = useRecoilValue(appDataAtomByPoolType("app"));
  const transformerData = useRecoilValue(appDataAtomByPoolType("transformer"));

  const [selectedAppId, setSelectedAppId] = useState(selected);

  const apps = appData.filter((app) => !disabledAppIds.includes(app.appId));
  const transformers = transformerData.filter(
    (app) => !disabledAppIds.includes(app.appId)
  );

  return (
    <Flex
      css={css`
        padding: ${rem(39)};
        border-radius: ${rem(24)};
        background: linear-gradient(
            180deg,
            ${transparentize(0.96, color.highlight)} 0%,
            ${transparentize(1, color.highlight)} 39.2%
          ),
          ${lighten(0.48, color.highlight)};
        overflow: scroll;
      `}
      justify="flex-start"
    >
      {includeSelf ? (
        <AppButton
          key="self"
          action={actionText}
          apy={"0.00"}
          iconProps={{
            dashed: true,
            noShadow: true,
            css: css`
              border-color: ${color.highlight};
            `,
          }}
          logo={
            <AmpMark
              color={color.highlight}
              css={css`
                width: ${rem(36)};
              `}
            />
          }
          name="wallet"
          selected={selectedAppId === "self"}
          onClick={() => {
            setSelectedAppId("self");
            onSelect && onSelect("self");
          }}
        />
      ) : null}

      {apps.map((app) => {
        if (app.slug === "gemini") {
          return <></>;
        }
        return (
          <AppButtonWithAPY
            key={app.appId}
            action={actionText}
            appId={app.appId}
            logo={app.logo}
            name={app.name}
            selected={app.appId === selectedAppId}
            onClick={() => {
              setSelectedAppId(app.appId);
              onSelect && onSelect(app.appId);
            }}
          />
        );
      })}
      <div
        css={css`
          width: 1px;
          align-self: stretch;
          background-image: linear-gradient(
            17.51deg,
            var(--gradient-start) -8.9%,
            var(--gradient-end) 192.03%
          );
          opacity: 0.1;
          flex: 0 0 1px;
        `}
      />
      {transformers.map((app) => (
        <AppButtonWithAPY
          key={app.appId}
          action={actionText}
          appId={app.appId}
          iconProps={{
            logoTag: flexaLogoTag,
          }}
          logo={app.logo}
          name={app.name}
          selected={app.appId === selectedAppId}
          onClick={() => {
            setSelectedAppId(app.appId);
            onSelect && onSelect(app.appId);
          }}
        />
      ))}
    </Flex>
  );
};

export default AppSelector;
